import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const STATUS = {
  pendente: { l: "Pendente", c: "bg-[#FFCC00]" },
  aceito: { l: "Aceito", c: "bg-[#00A859] text-white" },
  recusado: { l: "Recusado", c: "bg-[#E60000] text-white" },
  substituido: { l: "Substituído", c: "bg-[#71717A] text-white" },
};

const FORM_VAZIO = {
  montador_id: "", periodicidade: "mensal", dia_pagamento: "", pagamento_dias: [],
  ciclo: null, todos_regiao: false, texto_proprio: "", aceite_empresa: false,
  forma_pagamento: "PIX", vigencia_inicio: "", observacoes: "",
};

export default function ContratosMontador() {
  const { user } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(user?.empresa_id || "");
  const [montadores, setMontadores] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [lista, setLista] = useState([]);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [itens, setItens] = useState([{ tipo_servico: "montagem", modo: "fixo", valor: "" }]);
  const [det, setDet] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") api.get("/empresas").then(r => setEmpresas(r.data));
    api.get("/montadores").then(r => setMontadores(r.data));
    api.get("/tipos-servico-padrao").then(r => setTipos(r.data));
  }, [user]);

  const load = async () => {
    if (!empresaId) return;
    const r = await api.get(`/empresas/${empresaId}/contratos-montador`);
    setLista(r.data);
  };
  useEffect(() => { load(); }, [empresaId]);

  const usarTabela = async () => {
    try {
      const r = await api.get(`/empresas/${empresaId}/tabela-valores`);
      const its = (r.data.itens || []).filter(i => (i.valor_montador ?? i.valor) > 0)
        .map(i => ({ tipo_servico: i.tipo_servico, modo: i.modo, valor: i.valor_montador ?? i.valor }));
      if (!its.length) { toast.error("Tabela de valores sem itens de montador"); return; }
      setItens(its);
      toast.success("Valores da tabela aplicados");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const salvar = async () => {
    if (!form.todos_regiao && !form.montador_id) { toast.error("Selecione o montador ou 'todos da região'"); return; }
    const dias = (form.pagamento_dias || []).filter(Boolean);
    const need = form.periodicidade === "quinzenal" ? 2 : 1;
    if (dias.length !== need) { toast.error("Selecione a(s) data(s) de pagamento"); return; }
    if (!form.aceite_empresa) { toast.error("Confirme que leu e autoriza o envio do contrato"); return; }
    setSalvando(true);
    try {
      const ciclo = form.ciclo && form.ciclo.inicio_dia && form.ciclo.fechamento_dia && form.ciclo.pagamento_dia ? form.ciclo : null;
      const r = await api.post(`/empresas/${empresaId}/contratos-montador`, {
        ...form, itens,
        pagamento_config: { periodicidade: form.periodicidade, dias, ciclo },
      });
      if (r.data.enviados !== undefined) {
        toast.success(`Contrato enviado a ${r.data.enviados} montador(es)` +
          (r.data.erros?.length ? ` · ${r.data.erros.length} pulado(s)` : ""));
      } else {
        toast.success("Contrato gerado e enviado ao montador para aceite");
      }
      setNovo(false); setForm(FORM_VAZIO);
      setItens([{ tipo_servico: "montagem", modo: "fixo", valor: "" }]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSalvando(false); }
  };

  const setItem = (i, patch) => setItens(itens.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap">Compliance · Empresa</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Contratos de Montadores</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Contrato comercial individual com valores, data de pagamento e aceite digital auditado (IP/dispositivo no log jurídico imutável).
          </p>
        </div>
        <Button onClick={() => setNovo(true)} disabled={!empresaId} className="btn-sharp brand-bg text-white"
          data-testid="ct-novo">+ Novo contrato</Button>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <Select value={empresaId} onValueChange={setEmpresaId}>
            <SelectTrigger className="max-w-md" data-testid="ct-empresa"><SelectValue placeholder="Selecione empresa" /></SelectTrigger>
            <SelectContent>{empresas.map(e => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F4F5] border-b border-border">
            <tr>
              <th className="text-left p-3 label-cap">Versão</th>
              <th className="text-left p-3 label-cap">Montador</th>
              <th className="text-left p-3 label-cap">Pagamento</th>
              <th className="text-left p-3 label-cap">Serviços</th>
              <th className="text-left p-3 label-cap">Status</th>
              <th className="text-left p-3 label-cap">Aceite em</th>
              <th className="text-left p-3 label-cap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">Nenhum contrato</td></tr>}
            {lista.map(c => {
              const st = STATUS[c.status] || STATUS.pendente;
              return (
                <tr key={c.id} className="border-b border-border last:border-0" data-testid={`ct-${c.id}`}>
                  <td className="p-3 font-mono text-xs">#{c.versao}</td>
                  <td className="p-3 font-medium">{c.montador_nome}</td>
                  <td className="p-3 text-xs capitalize">{c.periodicidade} · {c.dia_pagamento} · {c.forma_pagamento}</td>
                  <td className="p-3 text-xs">{(c.itens || []).length} tipo(s)</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-[10px] uppercase ${st.c}`}>{st.l}</span></td>
                  <td className="p-3 text-xs">{c.aceito_em ? new Date(c.aceito_em).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="p-3">
                    <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]"
                      onClick={() => setDet(c)} data-testid={`ct-ver-${c.id}`}>Ver</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={novo} onOpenChange={setNovo}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Novo contrato comercial</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-cap">Destinatários *</Label>
              <div className="flex gap-2 mt-1 mb-2">
                <button onClick={() => setForm({ ...form, todos_regiao: false })}
                  className={`px-3 py-1.5 rounded text-xs font-bold ${!form.todos_regiao ? "bg-[#142766] text-white" : "border border-border text-muted-foreground"}`}
                  data-testid="ct-destino-um">Um montador</button>
                <button onClick={() => setForm({ ...form, todos_regiao: true, montador_id: "" })}
                  className={`px-3 py-1.5 rounded text-xs font-bold ${form.todos_regiao ? "bg-[#142766] text-white" : "border border-border text-muted-foreground"}`}
                  data-testid="ct-destino-todos">TODOS os montadores da minha região</button>
              </div>
              {!form.todos_regiao ? (
                <Select value={form.montador_id} onValueChange={(v) => setForm({ ...form, montador_id: v })}>
                  <SelectTrigger data-testid="ct-montador"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{montadores.map(m => <SelectItem key={m.id} value={m.id}>{m.name || m.nome}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="text-xs text-muted-foreground border border-border rounded p-2">
                  O contrato será enviado a todos os montadores ativos nos estados/cidades da sua área de atuação.
                  Montadores com contrato pendente serão pulados.
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="label-cap">Valores por tipo de serviço *</Label>
                <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]" onClick={usarTabela}
                  data-testid="ct-usar-tabela">Usar tabela de valores</Button>
              </div>
              <div className="space-y-2">
                {itens.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_120px_32px] gap-2 items-center">
                    <Select value={it.tipo_servico} onValueChange={(v) => setItem(i, { tipo_servico: v })}>
                      <SelectTrigger data-testid={`ct-item-tipo-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{tipos.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={it.modo} onValueChange={(v) => setItem(i, { modo: v })}>
                      <SelectTrigger data-testid={`ct-item-modo-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixo">R$ fixo</SelectItem>
                        <SelectItem value="percentual">% produto</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.01" min="0" placeholder="Valor" value={it.valor}
                      onChange={(e) => setItem(i, { valor: e.target.value })} data-testid={`ct-item-valor-${i}`} />
                    <button onClick={() => setItens(itens.filter((_, idx) => idx !== i))}
                      className="text-muted-foreground hover:text-[#E60000]" disabled={itens.length === 1}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px] mt-2"
                onClick={() => setItens([...itens, { tipo_servico: "assistencia", modo: "fixo", valor: "" }])}
                data-testid="ct-add-item"><Plus size={12} className="mr-1" /> Adicionar serviço</Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-cap">Periodicidade *</Label>
                <Select value={form.periodicidade} onValueChange={(v) => setForm({ ...form, periodicidade: v, pagamento_dias: [] })}>
                  <SelectTrigger data-testid="ct-periodicidade"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semanal">Semanal</SelectItem>
                    <SelectItem value="quinzenal">Quinzenal</SelectItem>
                    <SelectItem value="mensal">Mensal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-cap">
                  {form.periodicidade === "semanal" ? "Dia da semana do pagamento *"
                    : form.periodicidade === "quinzenal" ? "Os 2 dias do mês *" : "Dia do mês do pagamento *"}
                </Label>
                {form.periodicidade === "semanal" ? (
                  <Select value={form.pagamento_dias[0] || ""} onValueChange={(v) => setForm({ ...form, pagamento_dias: [v] })}>
                    <SelectTrigger data-testid="ct-dia-semana"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {["segunda", "terca", "quarta", "quinta", "sexta", "sabado"].map(d => (
                        <SelectItem key={d} value={d}>{d === "sabado" ? "Sábado" : d.charAt(0).toUpperCase() + d.slice(1) + "-feira"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    {(form.periodicidade === "quinzenal" ? [0, 1] : [0]).map(idx => (
                      <Select key={idx} value={String(form.pagamento_dias[idx] || "")}
                        onValueChange={(v) => {
                          const dias = [...form.pagamento_dias]; dias[idx] = Number(v);
                          setForm({ ...form, pagamento_dias: dias });
                        }}>
                        <SelectTrigger data-testid={`ct-dia-mes-${idx}`}><SelectValue placeholder={`Dia ${idx + 1}`} /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <SelectItem key={d} value={String(d)}>Dia {d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="label-cap">Forma de pagamento</Label>
                <Select value={form.forma_pagamento} onValueChange={(v) => setForm({ ...form, forma_pagamento: v })}>
                  <SelectTrigger data-testid="ct-forma"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PIX">PIX (chave do cadastro do montador)</SelectItem>
                    <SelectItem value="TED">TED</SelectItem>
                    <SelectItem value="Boleto">Boleto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-cap">Início da vigência</Label>
                <Input type="date" value={form.vigencia_inicio}
                  onChange={(e) => setForm({ ...form, vigencia_inicio: e.target.value })} data-testid="ct-vigencia" />
              </div>
            </div>

            <div className="border border-border rounded p-3">
              <Label className="label-cap">Ciclo de fechamento (para fins de contrato)</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[["inicio_dia", "Início (dia)"], ["fechamento_dia", "Fechamento (dia)"], ["pagamento_dia", "Pagamento (dia)"]].map(([k, lbl]) => (
                  <Select key={k} value={String(form.ciclo?.[k] || "")}
                    onValueChange={(v) => setForm({ ...form, ciclo: { ...(form.ciclo || {}), [k]: Number(v) } })}>
                    <SelectTrigger data-testid={`ct-ciclo-${k}`}><SelectValue placeholder={lbl} /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <SelectItem key={d} value={String(d)}>Dia {d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Ex.: início dia 1, fechamento dia 15, pagamento dia 25 do mesmo mês ou subsequente.
              </p>
            </div>

            <div className="border border-border rounded p-3">
              <Label className="label-cap">Contrato próprio (opcional)</Label>
              <Textarea rows={4} value={form.texto_proprio || ""} placeholder="Cole aqui o texto do SEU modelo de contrato. Se vazio, usamos o modelo MontaJá."
                onChange={(e) => setForm({ ...form, texto_proprio: e.target.value })} data-testid="ct-texto-proprio" />
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Mesmo com contrato próprio, valores, datas, nome do montador, PIX e periodicidade oficiais são anexados e prevalecem.
              </p>
            </div>

            <div>
              <Label className="label-cap">Disposições específicas (opcional — ex.: garantia de 30 dias, troca total)</Label>
              <Textarea rows={3} value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="ct-observacoes" />
            </div>

            <label className="flex items-start gap-2 text-xs border border-[#FFCC00] bg-[#FFFBEB] rounded p-3 cursor-pointer">
              <input type="checkbox" checked={!!form.aceite_empresa}
                onChange={(e) => setForm({ ...form, aceite_empresa: e.target.checked })}
                data-testid="ct-aceite-empresa" />
              <span><strong>Li, visualizei e autorizo</strong> o envio deste contrato aos montadores. Este aceite fica registrado e auditado.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovo(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={salvando} className="brand-bg text-white" data-testid="ct-salvar">
              {salvando ? "Gerando contrato…" : "Gerar e enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!det} onOpenChange={(o) => !o && setDet(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Contrato #{det?.versao} · {det?.montador_nome}</DialogTitle></DialogHeader>
          {det && (
            <div className="space-y-3 text-sm">
              <div className="border-l-4 border-[#142766] p-4 bg-[#F4F4F5] whitespace-pre-wrap font-mono text-xs leading-relaxed"
                data-testid="ct-texto">{det.texto}</div>
              {det.aceito_em && (
                <div className="border-l-4 border-[#00A859] p-3 bg-green-50 text-xs">
                  ✓ Aceito em {new Date(det.aceito_em).toLocaleString("pt-BR")}<br/>
                  IP: {det.ip_aceite} · {det.dispositivo_aceite?.slice(0, 80)}…
                </div>
              )}
              {det.recusado_em && (
                <div className="border-l-4 border-[#E60000] p-3 bg-red-50 text-xs">
                  ✗ Recusado em {new Date(det.recusado_em).toLocaleString("pt-BR")}<br/>
                  Motivo: {det.motivo_recusa}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
